import { useQuery } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import { ReactElement } from "react";

import {
  GetSubscriberUploadInvalidRowsSignedDownloadUrlQuery,
  GetSubscriberUploadInvalidRowsSignedDownloadUrlQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { GET_SUBSCRIBER_UPLOAD_INVALID_ROWS_SIGNED_DOWNLOAD_URL } from "~frontend/graphql/shortcode";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import { plural } from "~frontend/util/plural";

import { AudienceContactImportJob } from "./types";

function JobDetails({
  job,
  isOptInAudience,
}: {
  job: AudienceContactImportJob;
  isOptInAudience: boolean;
}) {
  const { subaccount } = useSubaccount()!;

  const { data, loading } = useQuery<
    GetSubscriberUploadInvalidRowsSignedDownloadUrlQuery,
    GetSubscriberUploadInvalidRowsSignedDownloadUrlQueryVariables
  >(GET_SUBSCRIBER_UPLOAD_INVALID_ROWS_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        trackedJobId: job.id,
        subaccountId: subaccount.id,
      },
    },
    skip: jobInProgress(job) || !job.result?.nInvalidRows,
  });

  if (jobFailed(job).failed) {
    return (
      <Box>
        <Alert severity="error">
          Failed to import subscribers: {jobFailed(job).errorMessage}
        </Alert>
      </Box>
    );
  }

  if (jobInProgress(job) || !job.result || loading) {
    return (
      <Box>
        <Typography>
          Importing subscribers from {job.args.upload.fileName}
        </Typography>
        <Loading />
      </Box>
    );
  }

  const nRowsUploaded = isOptInAudience
    ? job.result.nExistingSubscribersUpdated +
      job.result.nNewSubscribers +
      job.result.nInvalid +
      job.result.nDuplicates +
      job.result.nOptedOutSubscribers
    : job.result.nExistingSubscribersUpdated +
      job.result.nNewSubscribers +
      (job.result.nInvalid - job.result.nInvalidRows);

  return (
    <FlexBox
      flexDirection="column"
      gap={2}
      data-testid="import-subscribers-job-details"
    >
      <FlexBox
        flexDirection="column"
        gap={2}
        sx={{ backgroundColor: palette.lightestGray, p: 2 }}
      >
        <Typography variant="body1">
          {plural(nRowsUploaded, "row", "rows")} uploaded from{" "}
          {job.args.upload.fileName}
        </Typography>

        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Phone number column: {job.args.phoneNumberColumn}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            First name column: {job.args.firstNameColumn || "none"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Last name column: {job.args.lastNameColumn || "none"}
          </Typography>
          {job.args.silentBackfill && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Initial welcome messages skipped (backfill mode)
            </Typography>
          )}
        </Box>
        {isOptInAudience ? (
          <>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {plural(
                  job.result.nInvalid,
                  "invalid number",
                  "invalid numbers"
                )}{" "}
                skipped
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {plural(
                  job.result.nDuplicates,
                  "duplicate number",
                  "duplicate numbers"
                )}{" "}
                skipped
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {plural(
                  job.result.nOptedOutSubscribers,
                  "opted out number",
                  "opted out numbers"
                )}{" "}
                skipped
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {
                  // nInvalid encompasses both invalid rows and invalid tns, so we subtract to get
                  // just the invalid numbers
                  plural(
                    job.result.nInvalid - job.result.nInvalidRows,
                    "invalid number",
                    "invalid numbers"
                  )
                }{" "}
                skipped
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {plural(
                  job.result.nDuplicates,
                  "duplicate number",
                  "duplicate numbers"
                )}{" "}
                skipped
              </Typography>
            </Box>
          </>
        )}
        <Typography variant="body1Bold" sx={{ mt: 2 }}>
          {plural(job.result.nNewSubscribers, "new contact", "new contacts")}{" "}
          added
        </Typography>
        <Typography variant="body1Bold">
          {plural(
            job.result.nExistingSubscribersUpdated,
            "existing contact",
            "existing contacts"
          )}{" "}
          updated
        </Typography>
      </FlexBox>

      {job.result.nInvalidRows > 0 && (
        <Alert variant="outlined" severity="error">
          <AlertTitle color="error">
            {plural(job.result.nInvalidRows, "contact", "contacts")} with
            invalid field values
          </AlertTitle>
          <FlexBox gap={2} alignItems="flex-end">
            <Typography variant="body3">
              {job.result.nInvalidRows > 1
                ? "These contacts were"
                : "This contact was"}{" "}
              uploaded with the invalid fields omitted. If you wish to correct
              this, download the CSV to the right, correct the invalid fields,
              and reupload to update the contact{" "}
              {job.result.nInvalidRows > 1 ? "records" : "record"}.
            </Typography>

            {data && (
              <ButtonLink
                variant="contained"
                color="secondary"
                sx={{ minWidth: "110px" }}
                to={data!.getSubscriberUploadInvalidRowsSignedDownloadUrl}
              >
                Download
              </ButtonLink>
            )}
          </FlexBox>
        </Alert>
      )}
    </FlexBox>
  );
}

export default function JobDetailsDialog({
  job,
  onClose,
  isOptInAudience = true,
}: {
  job: AudienceContactImportJob;
  onClose: () => void;
  isOptInAudience?: boolean;
}): ReactElement {
  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isOptInAudience ? "Subscriber" : "Contact"} Upload Summary
      </DialogTitle>
      <DialogContent>
        <JobDetails job={job} isOptInAudience={isOptInAudience} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="tertiary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
